<template>
  <TitanToggle>
    <template v-slot="{ on, toggle }">
      <div :class="['titan-accordion', `chevron-${position}`, { 'is-open': on }]" role="accordion">
        <header @click="onHeaderClick(on, toggle)">
          <div class="chevron" :class="{ open: on }" />
          <slot :toggle="toggle"></slot>
        </header>
        <!--
          Two render paths for the body:
          - `animate=false` (default): plain `v-if`. Existing behavior —
            no transition, snap show/hide. Untouched for AddonStatusTable.
          - `animate=true`: wrap in <transition> with JS hooks that animate
            `height` from 0 ↔ scrollHeight. Auto-height can't be animated
            with pure CSS, so we measure and toggle inline height in JS;
            the listener cleans up after one `transitionend`.
        -->
        <template v-if="!animate">
          <slot v-if="on" name="content" />
        </template>
        <transition
          v-else
          name="titan-accordion-collapse"
          @before-enter="onBeforeEnter"
          @enter="onEnter"
          @after-enter="onAfterEnter"
          @before-leave="onBeforeLeave"
          @leave="onLeave"
        >
          <div v-if="on" class="titan-accordion__collapse">
            <slot name="content" />
          </div>
        </transition>
      </div>
    </template>
  </TitanToggle>
</template>

<script>
import TitanToggle from "./TitanToggle";

/**
 * Generic accordion. Default-slot is the always-visible header; the
 * named `content` slot is the body shown when expanded. The toggle
 * primitive (TitanToggle) owns the open/closed state — callers don't
 * manage it.
 *
 * Props:
 *   - `position` ("left" | "right"): chevron placement.
 *       "left" (default) keeps the existing AddonStatusTable behavior.
 *       "right" pushes the chevron to the end of the header row.
 *   - `animate` (Boolean, default false): when true the body smoothly
 *       expands/collapses on toggle (height 0 ↔ auto via JS hooks).
 *       Opt-in so existing consumers see no behavior change.
 */
export default {
  props: {
    position: {
      type: String,
      default: "left",
      validator(prop) {
        return ["left", "right"].indexOf(prop) > -1;
      },
    },
    animate: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    TitanToggle,
  },
  methods: {
    /**
     * Toggle + announce the NEW open state. Additive `toggle` emit so hosts
     * (e.g. MobileDesktopAppAccordion analytics) can observe expansion;
     * existing consumers that don't listen are unaffected.
     */
    onHeaderClick(on, toggle) {
      toggle();
      this.$emit("toggle", !on);
    },
    onItemClick(item, toggle) {
      this.$emit("click", item, toggle);
    },
    // --- transition hooks -------------------------------------------------
    // Pattern: `height: auto` is not animatable, so we snapshot the
    // natural height with `scrollHeight`, transition explicit pixel
    // values, then clear the inline height once the animation lands so
    // the panel can grow with future content changes.
    onBeforeEnter(el) {
      el.style.height = "0px";
    },
    onEnter(el) {
      // requestAnimationFrame so the browser commits the 0px state
      // before we change to scrollHeight — otherwise it'd skip straight
      // to the target and no transition runs.
      requestAnimationFrame(() => {
        el.style.height = `${el.scrollHeight}px`;
      });
    },
    onAfterEnter(el) {
      el.style.height = "";
    },
    onBeforeLeave(el) {
      el.style.height = `${el.scrollHeight}px`;
    },
    onLeave(el) {
      // Force a reflow so the browser registers the starting height
      // before we kick it to 0.
      // eslint-disable-next-line no-unused-expressions
      el.offsetHeight;
      requestAnimationFrame(() => {
        el.style.height = "0px";
      });
    },
  },
};
</script>

<style lang="scss" scoped>
// Library defaults are wrapped in `:where(...)` so they carry 0
// specificity. Combined with Vue's scoped `[data-v-x]` attribute they
// settle at 0,1,0 — which means any scoped host class on the accordion
// root (0,2,0) overrides them with plain CSS, no `::v-deep`, no
// variable list, no order-of-imports surprises. `:where()` ships in all
// evergreen browsers (Chrome 88+, Firefox 78+, Safari 14+).
:where(.titan-accordion) {
  background: var(--tabBgCol);
  border: solid 1px var(--noteBorderCol);
  padding: 1.25em 1.5em;
  position: relative;
  width: 100%;
}

:where(header) {
  display: flex;
  align-items: center;
  padding: 0 1em;
  cursor: pointer;
}

:where(.chevron) {
  height: 24px;
  width: 24px;
  background: url("./assets/chevron.svg") no-repeat;
  transition: transform 0.1s ease-in-out;
  margin-right: 1em;
  flex-shrink: 0;

  &.open {
    transform: rotateZ(90deg);
  }
}

// Right-aligned chevron variant: flex `order` flips the chevron past
// the default slot, and `margin-left: auto` pushes it to the end of
// the row. Default-left behavior is untouched.
:where(.chevron-right) header {
  .chevron {
    order: 1;
    margin-right: 0;
    margin-left: auto;
  }
}

// Collapse animation (only active when `animate` is set). `overflow:
// hidden` lets the inline `height` clip content during the run. Hosts
// can tune duration/easing by overriding `transition` on this class.
:where(.titan-accordion__collapse) {
  overflow: hidden;
  transition: height 0.2s ease-in-out;
}
</style>
